import React from "react";
import map from "../assets/map.png";
import statewise from "../assets/statewisepatient.png";
import districtwise from "../assets/distwisepatient.png";
import Navbar from "../layout/Navbar";
import Footer from "../layout/Footer";
import { MapPin, BarChart2, PieChart, ArrowRight, Info } from "lucide-react";

const AyushmanBharat = () => {
  const cards = [
    {
      image: map,
      title: "State-wise Patient Distribution",
      description: "View state-level patient distribution across India under the Ayushman Bharat scheme.",
      buttonText: "View Map Data",
      icon: <MapPin className="w-5 h-5" />
    },
    {
      image: statewise,
      title: "State-wise Statistical Analysis",
      description: "Detailed statistical overview of beneficiaries across different states of India.",
      buttonText: "View State Stats",
      icon: <BarChart2 className="w-5 h-5" />
    },
    {
      image: districtwise,
      title: "District-wise Breakdown",
      description: "District-level breakdown of patient enrolment and treatment statistics.",
      buttonText: "View District Data",
      icon: <PieChart className="w-5 h-5" />
    },
  ];

  return (
    <>
      <Navbar />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-teal-600 to-teal-700 py-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-pattern opacity-10"></div>
        <div className="max-w-7xl mx-auto px-4 relative z-10">
          <div className="flex flex-col items-center text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Ayushman Bharat Dashboard</h1>
            <div className="w-24 h-1 bg-white/60 rounded-full mb-6"></div>
            <p className="text-white text-lg max-w-2xl leading-relaxed">
              Comprehensive visualization of patient data and healthcare statistics under the
              Ayushman Bharat Pradhan Mantri Jan Arogya Yojana (PM-JAY) across India.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Dashboard Introduction */}
          <div className="mb-12 bg-white p-6 rounded-xl border border-gray-100">
            <div className="flex items-start">
              <div className="flex-shrink-0 bg-teal-100 p-3 rounded-lg mr-4">
                <Info className="w-6 h-6 text-teal-600" />
              </div>
              <div>
                <h2 className="text-2xl font-semibold text-gray-800 mb-3">Healthcare Statistics Dashboard</h2>
                <p className="text-gray-600 leading-relaxed">
                  Explore comprehensive data visualizations showing the distribution and impact of the Ayushman Bharat scheme
                  across different states and districts of India. These analytics provide valuable insights into healthcare
                  accessibility and utilization patterns.
                </p>
              </div>
            </div>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
            {cards.map((card, index) => (
              <div
                key={index}
                className="bg-white rounded-xl overflow-hidden border border-gray-100 hover:border-teal-200 transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Card Header */}
                <div className="bg-gradient-to-r from-teal-500 to-teal-600 px-5 py-4 flex items-center">
                  <div className="bg-white/20 p-2 rounded-lg mr-3">
                    {card.icon}
                  </div>
                  <h3 className="text-white font-medium text-lg">{card.title}</h3>
                </div>

                {/* Card Image */}
                <div className="p-6 bg-gray-50 border-b border-gray-100">
                  <div className="overflow-hidden rounded-lg">
                    <img
                      src={card.image}
                      alt={`Ayushman Bharat ${card.title}`}
                      className="w-full h-64 object-contain hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                  </div>
                </div>

                {/* Card Content */}
                <div className="p-6">
                  <p className="text-gray-600 mb-5">{card.description}</p>
                  <button className="w-full bg-teal-500 hover:bg-teal-600 text-white py-2.5 px-4 rounded-lg transition-colors duration-300 flex items-center justify-center font-medium">
                    {card.buttonText}
                    <ArrowRight className="h-5 w-5 ml-2" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* CSS for background pattern */}
      <style jsx>{`
        .bg-pattern {
          background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.2'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }
      `}</style>

      <Footer />
    </>
  );
};

export default AyushmanBharat;